import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

// Cardano Foundation off-chain token registry (mainnet). Public, CORS-enabled
// (access-control-allow-origin: *), so the browser calls it directly. Supplies
// the official human name, ticker and logo for REGISTERED tokens; most niche /
// CIP-68 assets have no entry and fall back to the monogram avatar + on-chain
// name. Never fabricates data.
const REGISTRY = "https://tokens.cardano.org/metadata/query";

// subject (policy_id + asset_name_hex) -> { name, ticker, logo, decimals } | null (known miss)
const cache = new Map();
const inflight = new Set();

const chunk = (arr, n) => {
  const out = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
};

async function fetchBatch(subjects) {
  const want = subjects.filter((s) => s && !cache.has(s) && !inflight.has(s));
  if (!want.length) return false;
  want.forEach((s) => inflight.add(s));
  let changed = false;
  await Promise.all(
    chunk(want, 50).map(async (group) => {
      try {
        const res = await fetch(REGISTRY, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ subjects: group, properties: ["name", "ticker", "logo", "decimals"] }),
        });
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        const got = new Set();
        (data.subjects || []).forEach((s) => {
          got.add(s.subject);
          const logo = s.logo?.value;
          cache.set(s.subject, {
            name: s.name?.value || null,
            ticker: s.ticker?.value || null,
            logo: logo ? `data:image/png;base64,${logo}` : null,
            decimals: s.decimals?.value ?? null,
          });
          changed = true;
        });
        // Record misses so we never re-query them.
        group.forEach((subj) => { if (!got.has(subj)) { cache.set(subj, null); changed = true; } });
      } catch {
        // Leave uncached so a later request can retry transient failures.
      } finally {
        group.forEach((s) => inflight.delete(s));
      }
    }),
  );
  return changed;
}

const DEFAULT = { meta: cache, request: () => {}, version: 0 };
const Ctx = createContext(DEFAULT);

export const TokenMetaProvider = ({ children }) => {
  const [version, setVersion] = useState(0);
  const queueRef = useRef(new Set());
  const timerRef = useRef(null);

  const flush = useCallback(async () => {
    const subs = [...queueRef.current];
    queueRef.current.clear();
    const changed = await fetchBatch(subs);
    if (changed) setVersion((v) => v + 1);
  }, []);

  const request = useCallback((subjects) => {
    let added = false;
    (subjects || []).forEach((s) => { if (s && !cache.has(s)) { queueRef.current.add(s); added = true; } });
    if (!added) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, 180);
  }, [flush]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return <Ctx.Provider value={{ meta: cache, request, version }}>{children}</Ctx.Provider>;
};

export const useTokenMeta = () => useContext(Ctx);

// Register a list of subjects for background fetching; re-runs when the set changes.
export const useRequestMeta = (subjects) => {
  const { request } = useTokenMeta();
  const key = (subjects || []).filter(Boolean).join(",");
  useEffect(() => { if (key) request(key.split(",")); }, [request, key]);
};
